import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad, useTriggerLoad } from "@redotech/react-util/load";
import { PaymentProviderType } from "@redotech/redo-billing-common/billing-engine-types";
import { isInvoiceBillingActive } from "@redotech/redo-billing-common/tracking/tracking-billing-utils";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import { RedoOrderTrackingRpcClientContext } from "@redotech/redo-merchant-app-common/order-tracking-rpc-client-provider";
import {
  ReloadTeamContext,
  TeamContext,
} from "@redotech/redo-merchant-app-common/team";
import { Provider } from "@redotech/redo-model/order";
import { GeneralPermissions } from "@redotech/redo-model/permissions";
import { ShopifySubscriptionType } from "@redotech/redo-model/shopify-billing/shopify-subscription-types";
import { getUsagePlanPricing } from "@redotech/redo-model/shopify-subscription/shopify-subscription";
import { NotificationBannerItem } from "@redotech/redo-web/arbiter-components/notification/redo-notification-banner";
import { TeamContextValue } from "@redotech/redo-web/context/team-context";
import { filterTruthy } from "@redotech/util/array";
import { assertNever } from "@redotech/util/type";
import { useContext, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  confirmShopifySubscription,
  createShopifySubscription,
} from "../../../client/shopify-billing";
import { ActionRequiredSeverity } from "./action-required-notification";

export const newProductUrlParam = "new_product";

const SUPPORT_PRODUCTS = [
  {
    type: ShopifySubscriptionType.SUPPORT_AI,
    link: "/settings/billing?currentTab=support",
  },
  {
    type: ShopifySubscriptionType.SUPPORT_BASE,
    link: "/settings/billing?currentTab=support",
  },
  {
    type: ShopifySubscriptionType.TEXT_MESSAGING,
    link: "/settings/billing?currentTab=support",
  },
];

export function useShopifyBillingNotification():
  | { severity: ActionRequiredSeverity; item: NotificationBannerItem }[]
  | null {
  const location = useLocation();
  const navigate = useNavigate();

  const shopifyUrlParam = "charge_id";

  const dismissResultNotification = () => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete(shopifyUrlParam);
    searchParams.delete(newProductUrlParam);
    navigate({ search: searchParams.toString() });
  };

  const rpcClient = useRequiredContext(RedoOrderTrackingRpcClientContext);
  const team = useRequiredContext(TeamContext);

  const canAuthenticateWithThirdParties = useHasPermission(
    GeneralPermissions.AUTHENTICATE_WITH_THIRD_PARTIES,
  );

  const providers = useLoad(
    async (signal: AbortSignal) => {
      if (!canAuthenticateWithThirdParties) {
        return null;
      }
      const providers = await rpcClient.getProvidersForTeam(
        { teamId: team._id.toString() },
        { signal },
      );
      return providers;
    },
    [rpcClient, team._id, canAuthenticateWithThirdParties],
  );

  const queryParams = new URLSearchParams(location.search);
  const chargeId = queryParams.get(shopifyUrlParam);

  const shopifySubscription = team.settings.shopifySubscription?.[0];

  const usageComponent = getUsagePlanPricing(shopifySubscription);

  const activeBillableProducts: ShopifySubscriptionType[] = useMemo(() => {
    const activeBillableProducts = [];
    const emailMarketingBilling = team.settings.marketing?.emailBilling;
    const smsMarketingBilling = team.settings.marketing?.smsBilling;
    const recoverBilling = team.settings.recover?.billing;
    if (
      (emailMarketingBilling?.enabled &&
        (emailMarketingBilling.paymentProvider ??
          PaymentProviderType.SHOPIFY) === PaymentProviderType.SHOPIFY) ||
      (smsMarketingBilling?.enabled &&
        (smsMarketingBilling.paymentProvider ?? PaymentProviderType.SHOPIFY) ===
          PaymentProviderType.SHOPIFY)
    ) {
      activeBillableProducts.push(ShopifySubscriptionType.MARKETING);
    }

    if (
      recoverBilling?.enabled &&
      (recoverBilling.paymentProvider ?? PaymentProviderType.SHOPIFY) ===
        PaymentProviderType.SHOPIFY
    ) {
      activeBillableProducts.push(ShopifySubscriptionType.RECOVER);
    }

    if (doesOtNeedBillingAccepted(team)) {
      activeBillableProducts.push(ShopifySubscriptionType.ORDER_TRACKING);
    }

    if (
      team.settings.orderTracking?.postPurchaseUpsell?.enabled &&
      team.settings.orderTracking?.postPurchaseUpsell?.billing?.billingEnabled
    ) {
      activeBillableProducts.push(ShopifySubscriptionType.ONE_CLICK_UPSELL);
    }

    const checkoutOptimizationBilling =
      team.settings.checkoutOptimization?.checkoutOptimizationBilling;
    if (checkoutOptimizationBilling?.enabled) {
      activeBillableProducts.push(
        ShopifySubscriptionType.CHECKOUT_OPTIMIZATION,
      );
    }

    if (
      team.settings.returnsAiExchange.enableBilling &&
      team.settings.returnsAiExchange.renewOnPeriodEnd
    ) {
      activeBillableProducts.push(ShopifySubscriptionType.AI_EXCHANGE_AGENT);
    }

    if (
      team.settings.returns?.subscriptionBilling?.enabled &&
      (team.settings.returns?.subscriptionBilling?.paymentProvider ??
        PaymentProviderType.SHOPIFY) === PaymentProviderType.SHOPIFY
    ) {
      activeBillableProducts.push(ShopifySubscriptionType.RETURNS);
    }

    const disputesBilling = team.settings.disputes?.billing;
    if (
      disputesBilling?.enabled &&
      (disputesBilling.paymentProvider ?? PaymentProviderType.SHOPIFY) ===
        PaymentProviderType.SHOPIFY
    ) {
      activeBillableProducts.push(ShopifySubscriptionType.DISPUTES);
    }

    return activeBillableProducts;
  }, [team]);

  // We want to show pricing details before they accept so these will be accepted from their pages rather than the billing button.
  const supportBillableProducts: ShopifySubscriptionType[] = useMemo(() => {
    const supportBillableProducts = [];
    const baseFreeTrialEndDate =
      team.settings.support?.billing?.base?.freeTrialEndDate;
    const isInBaseFreeTrial =
      !!baseFreeTrialEndDate && new Date(baseFreeTrialEndDate) > new Date();
    if (
      team.settings.support?.enabled &&
      (team.settings.support?.billing?.base?.pricePerMonthInCents || 0) > 0 &&
      !isInBaseFreeTrial &&
      (!team.settings.support?.billing?.base?.subscriptionActive ||
        !team.settings?.shopifySubscription?.[0])
    ) {
      supportBillableProducts.push(ShopifySubscriptionType.SUPPORT_BASE);
    }
    const aiFreeTrialEndDate =
      team.settings.support?.billing?.ai?.freeTrialEndDate;
    const isInAiFreeTrial =
      !!aiFreeTrialEndDate && new Date(aiFreeTrialEndDate) > new Date();
    if (
      team.settings.support?.ai?.enabled &&
      !team.settings.support?.billing?.ai?.billingAccepted &&
      !isInAiFreeTrial
    ) {
      supportBillableProducts.push(ShopifySubscriptionType.SUPPORT_AI);
    }

    if (
      team.settings.support?.billing?.textMessaging?.enabled &&
      !team.settings.support?.billing?.textMessaging?.subscriptionActive
    ) {
      supportBillableProducts.push(ShopifySubscriptionType.TEXT_MESSAGING);
    }
    return supportBillableProducts;
  }, [team]);

  const confirmationNotification = useConfirmationNotification({
    activeBillableProducts,
    chargeId: chargeId ?? undefined,
    onDismiss: dismissResultNotification,
  });

  const supportProductsNeedingConsentInRedo = SUPPORT_PRODUCTS.find(
    (product) => {
      return supportBillableProducts.includes(product.type);
    },
  );
  const showUsageComponentAcceptBillingNotification =
    !usageComponent && activeBillableProducts.length > 0;

  const setupBilling = useSetupBillingAction();

  const usageComponentAcceptBillingNotification: {
    severity: ActionRequiredSeverity;
    item: NotificationBannerItem;
  } | null = useMemo(() => {
    if (!showUsageComponentAcceptBillingNotification) {
      return null;
    }
    const acceptString = `Accept billing to gain access to the following features: ${activeBillableProducts.map(productName).join(", ")}`;

    return {
      severity: ActionRequiredSeverity.Medium,
      item: {
        id: "shopify-billing-usage-component-accept-billing",
        message: acceptString,
        type: "warning",
        actions: [
          {
            actionType: "button",
            hierarchy: "primary",
            onClick: () => setupBilling.onClick(),
            text: "Accept billing",
            pending: setupBilling.pending,
          },
        ],
      },
    };
  }, [
    activeBillableProducts,
    setupBilling,
    showUsageComponentAcceptBillingNotification,
  ]);

  const supportBillingNotification: {
    severity: ActionRequiredSeverity;
    item: NotificationBannerItem;
  } | null = useMemo(() => {
    if (!supportProductsNeedingConsentInRedo) {
      return null;
    }

    const searchParams = new URLSearchParams(location.search);

    const completePath = `/stores/${team._id}${supportProductsNeedingConsentInRedo.link}`;

    const [path, queryString] = completePath.split("?");
    const newParams = new URLSearchParams(queryString || "");

    for (const [key, value] of newParams.entries()) {
      searchParams.set(key, value);
    }

    return {
      severity: ActionRequiredSeverity.Medium,
      item: {
        id: "shopify-billing-support-accept-billing",
        message: `Accept billing to gain access to: ${productName(
          supportProductsNeedingConsentInRedo.type,
        )}`,
        type: "warning",
        actions: [
          {
            href: `${path}?${searchParams}`,
            actionType: "link",
            target: "_self",
            text: "Accept billing",
            internal: true,
          },
        ],
      },
    };
  }, [location.search, supportProductsNeedingConsentInRedo, team._id]);

  const capRaiseBanner: {
    severity: ActionRequiredSeverity;
    item: NotificationBannerItem;
  } | null = useMemo(() => {
    const capRaiseUrl =
      team.settings.shopifySubscriptionMetadata?.pendingCapRaiseUrl;

    if (!capRaiseUrl) {
      return null;
    }

    return {
      severity: ActionRequiredSeverity.Medium,
      item: {
        id: "shopify-billing-cap-raise",
        message: "You are approaching or have hit your Shopify usage limit.",
        type: "warning",
        actions: [
          {
            actionType: "link",
            href: capRaiseUrl,
            text: "Raise limit",
            internal: false,
          },
        ],
      },
    };
  }, [team.settings.shopifySubscriptionMetadata?.pendingCapRaiseUrl]);

  if (
    !team ||
    !providers.value?.some((provider) => provider === Provider.SHOPIFY)
  ) {
    return null;
  }

  return filterTruthy([
    confirmationNotification,
    usageComponentAcceptBillingNotification,
    supportBillingNotification,
    capRaiseBanner,
  ]);
}

function productName(type: ShopifySubscriptionType): string {
  switch (type) {
    case ShopifySubscriptionType.SUPPORT_BASE:
      return "Support";
    case ShopifySubscriptionType.SUPPORT_AI:
      return "Support AI";
    case ShopifySubscriptionType.ORDER_TRACKING:
      return "Order Tracking";
    case ShopifySubscriptionType.TEXT_MESSAGING:
      return "SMS/MMS";
    case ShopifySubscriptionType.ONE_CLICK_UPSELL:
      return "One-click upsell";
    case ShopifySubscriptionType.MARKETING:
      return "Marketing";
    case ShopifySubscriptionType.RECOVER:
      return "Recover";
    case ShopifySubscriptionType.CHECKOUT_OPTIMIZATION:
      return "Checkout personalization";
    case ShopifySubscriptionType.SUPPORT_VOICE:
      return "Support Voice";
    case ShopifySubscriptionType.AI_EXCHANGE_AGENT:
      return "AI Exchange Agent";
    case ShopifySubscriptionType.RETURNS:
      return "Returns";
    case ShopifySubscriptionType.AEO:
      return "AEO";
    case ShopifySubscriptionType.IMS:
      return "IMS";
    case ShopifySubscriptionType.DISPUTES:
      return "Disputes";
    default:
      assertNever(type);
  }
}

function useConfirmationNotification({
  activeBillableProducts,
  chargeId,
  onDismiss,
}: {
  activeBillableProducts: ShopifySubscriptionType[];
  chargeId: string | undefined;
  onDismiss: () => void;
}): { severity: ActionRequiredSeverity; item: NotificationBannerItem } | null {
  const client = useRequiredContext(RedoMerchantClientContext);
  const reloadTeamContext = useContext(ReloadTeamContext);

  const [confirmLoad, doConfirm] = useTriggerLoad(async (signal) => {
    if (!chargeId) {
      return false;
    }
    let appSubscriptions = [];
    const queryParams = new URLSearchParams(location.search);
    const newProducts = queryParams.getAll(newProductUrlParam);
    const productsEnabling = newProducts.length
      ? (newProducts as ShopifySubscriptionType[])
      : activeBillableProducts;
    appSubscriptions = await confirmShopifySubscription(client, {
      chargeId,
      productsEnabling,
      signal,
    });

    reloadTeamContext?.();
    return appSubscriptions?.length > 0;
  });

  const billingButton = useSetupBillingAction();

  useEffect(() => {
    if (!chargeId) {
      return;
    }
    doConfirm();
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chargeId]);

  if (!chargeId) {
    return null;
  }

  if (confirmLoad.pending) {
    return {
      severity: ActionRequiredSeverity.ActiveAction,
      item: {
        id: "shopify-billing-confirmation-pending",
        message: "Confirming Shopify subscription...",
        type: "info",
      },
    };
  }

  if (!confirmLoad.value) {
    return {
      severity: ActionRequiredSeverity.ActiveAction,
      item: {
        id: "shopify-billing-confirmation-failed",
        message: "Failed to confirm Shopify subscription.",
        type: "error",
        actions: [
          {
            actionType: "button",
            hierarchy: "primary",
            onClick: () => billingButton.onClick(),
            text: "Try again",
            pending: billingButton.pending,
          },
        ],
      },
    };
  }

  return {
    severity: ActionRequiredSeverity.ActiveAction,
    item: {
      id: "shopify-billing-confirmation-success",
      message: "Shopify subscription has been confirmed.",
      type: "success",
      actions: [
        {
          actionType: "button",
          hierarchy: "primary",
          onClick: onDismiss,
          text: "Dismiss",
        },
      ],
    },
  };
}

// In the future if we want to use this for other billing confirmations
// it should be refactored to be more generic than just order tracking.
function useSetupBillingAction() {
  const client = useRequiredContext(RedoMerchantClientContext);
  const [createLoad, doCreate] = useTriggerLoad(async (signal) => {
    const res = await createShopifySubscription(client, { signal });
    const url = res.confirmationUrl;
    if (url) {
      location.href = url;
    }
  });

  return { onClick: doCreate, pending: createLoad.pending };
}

function doesOtNeedBillingAccepted(
  team: NonNullable<TeamContextValue>,
): boolean {
  if (
    !team.settings.orderTracking?.billing ||
    !team.settings.orderTracking.enabled
  ) {
    return false;
  }

  if (isInvoiceBillingActive(team.settings.orderTracking.billing)) {
    return (
      (team.settings.orderTracking.billing.invoiceBilling.paymentProvider ??
        PaymentProviderType.SHOPIFY) === PaymentProviderType.SHOPIFY
    );
  }

  return !team.settings.orderTracking.billing.continueIfNotAccepted;
}
